import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';

const DEFAULT_SNAP_POINTS = [0.4, 0.7, 0.92];
const DISMISS_THRESHOLD = 0.2; // Below this fraction, dismiss the sheet
const SWIPE_VELOCITY_THRESHOLD = 0.4; // pixels per millisecond
const OVERSCROLL_RESISTANCE = 0.3; // Rubber-band effect above max snap

interface MobileBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  snapPoints?: number[]; // Array of heights as fractions (0.25, 0.5, 0.75, 1)
  initialSnap?: number; // Index into snapPoints
  showHandle?: boolean;
  showBackdrop?: boolean;
  className?: string;
}

export const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  snapPoints = DEFAULT_SNAP_POINTS,
  initialSnap,
  showHandle = true,
  showBackdrop = true,
  className = '',
}) => {
  const resolvedInitialSnap = initialSnap ?? 1;

  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const [currentSnapIndex, setCurrentSnapIndex] = useState(resolvedInitialSnap);
  const [isDragging, setIsDragging] = useState(false);
  // Store height as fraction of viewport (0-1+)
  const [sheetHeight, setSheetHeight] = useState(snapPoints[resolvedInitialSnap] ?? 0.7);

  // Touch tracking refs
  const startY = useRef(0);
  const startHeight = useRef(0);
  const startTime = useRef(0);
  const lastY = useRef(0);
  const isDraggingFromHandle = useRef(false);
  const isContentScrolledTop = useRef(true);

  // Reset to initial snap point when opened
  useEffect(() => {
    if (isOpen) {
      const targetHeight = snapPoints[resolvedInitialSnap] ?? 0.7;
      setCurrentSnapIndex(resolvedInitialSnap);
      setSheetHeight(targetHeight);
    }
  }, [isOpen, resolvedInitialSnap, snapPoints]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus management: trap focus inside sheet when open
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;

    const sheet = sheetRef.current;
    const focusableElements = sheet.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }, [isOpen]);

  // Find the nearest snap point considering velocity
  const findNearestSnapPoint = useCallback(
    (height: number, velocity: number): number => {
      // Velocity-based closing: fast downward fling
      if (velocity < -SWIPE_VELOCITY_THRESHOLD) {
        // Fling down - go to next lower snap or dismiss
        for (let i = snapPoints.length - 1; i >= 0; i--) {
          if (snapPoints[i] < height) {
            return i;
          }
        }
        return -1; // Dismiss
      }

      // Fast upward fling
      if (velocity > SWIPE_VELOCITY_THRESHOLD) {
        for (let i = 0; i < snapPoints.length; i++) {
          if (snapPoints[i] > height) {
            return i;
          }
        }
        return snapPoints.length - 1;
      }

      // Check dismiss threshold first
      if (height < DISMISS_THRESHOLD) {
        return -1;
      }

      // Find nearest snap point by distance
      let nearestIndex = 0;
      let minDistance = Math.abs(snapPoints[0] - height);

      for (let i = 1; i < snapPoints.length; i++) {
        const distance = Math.abs(snapPoints[i] - height);
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = i;
        }
      }

      return nearestIndex;
    },
    [snapPoints]
  );

  // Check if content is scrolled to top
  const checkContentScroll = useCallback(() => {
    if (contentRef.current) {
      isContentScrolledTop.current = contentRef.current.scrollTop <= 0;
    }
  }, []);

  // Handle touch start on drag handle
  const handleHandleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      isDraggingFromHandle.current = true;
      startY.current = e.touches[0].clientY;
      lastY.current = e.touches[0].clientY;
      startHeight.current = sheetHeight;
      startTime.current = Date.now();
      setIsDragging(true);
    },
    [sheetHeight]
  );

  // Handle touch start on content (for overscroll-to-drag behavior)
  const handleContentTouchStart = useCallback(
    (e: React.TouchEvent) => {
      checkContentScroll();
      startY.current = e.touches[0].clientY;
      lastY.current = e.touches[0].clientY;
      startHeight.current = sheetHeight;
      startTime.current = Date.now();
      isDraggingFromHandle.current = false;
    },
    [sheetHeight, checkContentScroll]
  );

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const deltaY = startY.current - currentY; // Positive = dragging up
      const deltaFraction = deltaY / window.innerHeight;
      const maxSnap = snapPoints[snapPoints.length - 1];

      // If dragging from content and content isn't at top, let content scroll
      if (!isDraggingFromHandle.current) {
        checkContentScroll();

        // If content is scrolled down, allow normal scrolling
        if (!isContentScrolledTop.current) {
          return;
        }

        // Content is at top and user is dragging down - start sheet drag
        if (deltaY < 0 && isContentScrolledTop.current) {
          if (!isDragging) {
            // Reset start position to current position for smooth transition
            startY.current = currentY;
            startHeight.current = sheetHeight;
            setIsDragging(true);
          }
        } else if (!isDragging) {
          return;
        }
      }

      let newHeight = startHeight.current + deltaFraction;

      // Apply rubber-band effect above max snap point
      if (newHeight > maxSnap) {
        const overshoot = newHeight - maxSnap;
        newHeight = maxSnap + overshoot * OVERSCROLL_RESISTANCE;
      }

      // Clamp minimum
      newHeight = Math.max(0, newHeight);

      setSheetHeight(newHeight);
      lastY.current = currentY;
    },
    [isDragging, sheetHeight, snapPoints, checkContentScroll]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    const endTime = Date.now();
    const deltaTime = Math.max(endTime - startTime.current, 1);
    const deltaY = startY.current - lastY.current; // positive = up
    const velocity = deltaY / deltaTime / window.innerHeight; // fraction/ms

    const nearestSnapIndex = findNearestSnapPoint(sheetHeight, velocity * 1000);

    if (nearestSnapIndex === -1) {
      onClose();
    } else {
      setCurrentSnapIndex(nearestSnapIndex);
      setSheetHeight(snapPoints[nearestSnapIndex]);
    }

    setIsDragging(false);
    isDraggingFromHandle.current = false;
  }, [isDragging, sheetHeight, findNearestSnapPoint, onClose, snapPoints]);

  // Handle touch cancel
  const handleTouchCancel = useCallback(() => {
    setIsDragging(false);
    isDraggingFromHandle.current = false;
    setSheetHeight(snapPoints[currentSnapIndex]);
  }, [currentSnapIndex, snapPoints]);

  if (!isOpen) return null;

  const heightPercent = sheetHeight * 100;
  const isAtMaxSnap = currentSnapIndex === snapPoints.length - 1;

  return (
    <>
      {/* Backdrop */}
      {showBackdrop && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={`fixed bottom-0 left-0 right-0 z-[70] bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl flex flex-col ${className}`}
        style={{
          height: `${heightPercent}vh`,
          willChange: isDragging ? 'transform, height' : 'auto',
          transition: isDragging ? 'none' : 'height 300ms cubic-bezier(0.32, 0.72, 0, 1)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          contain: 'layout style',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'bottom-sheet-title' : undefined}
        aria-label={title ? undefined : 'Bottom sheet'}
      >
        {/* Drag Handle */}
        {showHandle && (
          <div
            ref={handleRef}
            className="flex justify-center py-3 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
            onTouchStart={handleHandleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            role="slider"
            aria-orientation="vertical"
            aria-label="Drag to resize"
            aria-valuenow={Math.round(heightPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' && currentSnapIndex < snapPoints.length - 1) {
                const nextIndex = currentSnapIndex + 1;
                setCurrentSnapIndex(nextIndex);
                setSheetHeight(snapPoints[nextIndex]);
              } else if (e.key === 'ArrowDown' && currentSnapIndex > 0) {
                const nextIndex = currentSnapIndex - 1;
                setCurrentSnapIndex(nextIndex);
                setSheetHeight(snapPoints[nextIndex]);
              } else if (e.key === 'ArrowDown' && currentSnapIndex === 0) {
                onClose();
              }
            }}
          >
            <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
          </div>
        )}

        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2
              id="bottom-sheet-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        )}

        {/* Content */}
        <div
          ref={contentRef}
          className={`flex-1 min-h-0 ${isAtMaxSnap && !isDragging ? 'overflow-y-auto' : 'overflow-hidden'}`}
          style={{
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
          onTouchStart={handleContentTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          {children}
        </div>

        {/* Snap point indicator dots */}
        {snapPoints.length > 1 && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
            {snapPoints.map((point, index) => (
              <button
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  index === currentSnapIndex
                    ? 'bg-primary dark:bg-blue-400'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
                onClick={() => {
                  setCurrentSnapIndex(index);
                  setSheetHeight(snapPoints[index]);
                }}
                aria-label={`Snap to ${Math.round(point * 100)}% height`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MobileBottomSheet;
